import { Play } from "lucide-react";
import { formatDuration, parseTimestamp } from "./formatTime";
import { useGlobalPlayer } from "./GlobalPlayerContext";
import { qualifiedRecordingName } from "./recordingReference";
import type { PlayableRecording } from "./types";

/**
 * What a recording chip calls its take. On a song page the song is already
 * known, so the filename says which take; on a setlist page the song is what
 * matters, and its title reads as prose mid-sentence where
 * `pigeon_dreams_rough_2.mp3` would not.
 */
export type RecordingName = "filename" | "songTitle";

type Props = {
  recordingName: string;
  timestamp: string | null;
  recordings: PlayableRecording[];
  /** What the chip calls the take it resolves to; defaults to the filename */
  displayName?: RecordingName;
};

/**
 * Strip common audio extensions for matching purposes.
 */
function stripExtension(filename: string): string {
  return filename.replace(/\.(mp3|wav|m4a|aac|ogg|flac|webm)$/i, "");
}

/**
 * Find a recording by name with smart matching:
 * 1. Song-qualified match ("Song Title — filename"), written on multi-song
 *    pages where filenames alone can collide across songs
 * 2. Exact filename match (with or without extension)
 * 3. Partial filename match as fallback
 *
 * Ported to iOS as `FeedbackRecordingResolver`; the two must agree on which
 * take a reference means.
 */
function findRecording(
  recordings: PlayableRecording[],
  name: string,
): PlayableRecording | undefined {
  const normalizedName = stripExtension(name.trim()).toLowerCase();

  for (const r of recordings) {
    const qualified = qualifiedRecordingName(r.songTitle, r.filename);
    if (
      qualified !== null &&
      stripExtension(qualified).toLowerCase() === normalizedName
    ) {
      return r;
    }
  }

  // Try exact match first (ignoring extension)
  const exactMatch = recordings.find(
    (r) => stripExtension(r.filename).toLowerCase() === normalizedName,
  );
  if (exactMatch) return exactMatch;

  // Fall back to partial match
  return recordings.find((r) =>
    stripExtension(r.filename).toLowerCase().includes(normalizedName),
  );
}

/**
 * The name the chip prints for a take, never the reference as written. A
 * take whose song has no title falls back to its filename minus the
 * extension, as `Match.title` does on iOS.
 */
function chipName(recording: PlayableRecording, mode: RecordingName): string {
  if (mode === "filename") return recording.filename;
  return recording.songTitle.trim() || stripExtension(recording.filename);
}

/**
 * A clickable chip that plays a specific recording at an optional timestamp.
 * Rendered from parsed [Recording Name @ 1:30] markup in feedback.
 */
export function RecordingLink({
  recordingName,
  timestamp,
  recordings,
  displayName = "filename",
}: Props) {
  const { play, tracks, currentTrackIndex, isPlaying, pause, resume, seekTo } =
    useGlobalPlayer((s) => ({
      play: s.play,
      tracks: s.tracks,
      currentTrackIndex: s.currentTrackIndex,
      isPlaying: s.isPlaying,
      pause: s.pause,
      resume: s.resume,
      seekTo: s.seekTo,
    }));

  const matchingRecording = findRecording(recordings, recordingName);
  const timestampSeconds = timestamp ? parseTimestamp(timestamp) : null;

  // Check if this recording is currently playing
  const currentTrack =
    currentTrackIndex !== null ? tracks[currentTrackIndex] : null;
  const isThisPlaying =
    matchingRecording && currentTrack?.id === matchingRecording.id;

  const handleClick = () => {
    if (!matchingRecording) return;

    if (isThisPlaying) {
      // Already loaded: seek if timestamp provided, otherwise toggle play/pause
      if (timestampSeconds !== null) {
        seekTo(timestampSeconds);
        if (!isPlaying) resume();
      } else {
        isPlaying ? pause() : resume();
      }
      return;
    }

    // Start playback - reuse existing playlist position if available
    const existingIndex = tracks.findIndex(
      (t) => t.id === matchingRecording.id,
    );
    if (existingIndex !== -1) {
      play(tracks, existingIndex, false);
    } else {
      play(
        [
          {
            id: matchingRecording.id,
            filename: matchingRecording.filename,
            signedAudioUrl: matchingRecording.signedAudioUrl,
            songId: matchingRecording.songId,
            songTitle: matchingRecording.songTitle,
            authorName: matchingRecording.authorName,
          },
        ],
        0,
        false,
      );
    }

    if (timestampSeconds !== null) {
      seekTo(timestampSeconds);
    }
  };

  // If no matching recording found, render as plain text
  if (!matchingRecording) {
    return (
      <span className="font-mono text-text-muted">
        [{recordingName}
        {timestamp ? ` @ ${timestamp}` : ""}]
      </span>
    );
  }

  // align-middle sits the chip on the midline of the words around it, as
  // FlowLayout(alignment: .center) does on iOS; the inline-flex default
  // would hang it from the text baseline and ride high.
  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-1 rounded-full bg-amber/10 px-2 py-0.5 align-middle text-xs font-medium text-amber transition-colors hover:bg-amber/20"
    >
      <Play className="h-3 w-3" aria-hidden="true" />
      <span className="font-mono">
        {chipName(matchingRecording, displayName)}
      </span>
      {timestampSeconds !== null && (
        <span className="text-amber/70">
          @ {formatDuration(timestampSeconds)}
        </span>
      )}
    </button>
  );
}
